import {Dialog, DialogTitle, Flex, Text} from '@priority-software/priority-style-react';
import React, {forwardRef, useEffect, useImperativeHandle, useState} from 'react';
import {CreditCardForm} from './CreditCardForm';
import success from '../images/success.gif';
import error from '../images/error.png';
import {Loader} from '../../../features/OnboardingDialog/components';
import {procClientContinue} from '../../../_shared/GWTBridge/invoke/procMain';
import {BankTransferForm} from './BankTransferForm';
import {getBaseUrl, includeScriptFile} from '../../../_shared/GWTBridge/invoke/common';
import {devBluesnapURL, prodBluesnapURL} from '../consts';

interface BluesnapConfig {
    sum: number;
    token: string;
    isBankTrans?: boolean;
    isDev?: boolean;
    currency?: string;
}

export const BluesnapDialog = forwardRef((props: any, ref: any) => {
    const [isOpen, setIsOpen] = useState(false);
    const [config, setConfig] = useState<BluesnapConfig>(null);
    const [isScriptLoaded, setIsScriptLoaded] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const openDialog = config => {
        setConfig(config);
        setIsOpen(true);
    };

    const closeDialog = () => {
        setIsLoading(false);
        setIsSubmitted(false);
        setIsOpen(false);
        setConfig(null);
    };

    const onCloseHandler = () => {
        if (!isLoading && !isSubmitted) {
            setIsLoading(true);
            procClientContinue('');
        }
    };

    useImperativeHandle(
        ref,
        () => {
            return {
                openDialog: openDialog,
                hideDialog: closeDialog
            };
        },
        []
    );

    return (
        <>
            <Dialog
                width={563}
                isOpen={isOpen}
                closable={!isLoading && !isSubmitted}
                onClose={onCloseHandler}
                title={
                    <DialogTitle
                        title={
                            config?.isBankTrans ? 'Payment by ACH' : 'Add Your Credit Card Details'
                        }
                    />
                }
                content={
                    isOpen && (
                        <BluesnapDialogContent
                            sum={config.sum}
                            token={config.token}
                            isBankTrans={config.isBankTrans}
                            isDev={config.isDev}
                            isScriptLoaded={isScriptLoaded}
                            setIsScriptLoaded={setIsScriptLoaded}
                            isLoading={isLoading}
                            setIsLoading={setIsLoading}
                            setIsSubmitted={setIsSubmitted}
                            isSubmitted={isSubmitted}
                            currency={config.currency}
                        />
                    )
                }
            />
        </>
    );
});

const BluesnapDialogContent = ({
    sum,
    token,
    isBankTrans,
    isDev,
    isScriptLoaded,
    setIsScriptLoaded,
    isLoading,
    setIsLoading,
    isSubmitted,
    setIsSubmitted,
    currency
}) => {
    const [isSuccess, setIsSuccess] = useState(false);
    const [isError, setIsError] = useState(false);

    useEffect(() => {
        if (!isScriptLoaded) {
            includeScriptFile(getBaseUrl() + (isDev ? devBluesnapURL : prodBluesnapURL)).onload =
                () => {
                    setIsLoading(false);
                    setIsScriptLoaded(true);
                };
        } else {
            setIsLoading(false);
        }
    }, []);

    if (isSuccess || isError || isLoading) {
        return (
            <Flex style={{height: 506}} justifyContent="center" direction="column">
                {isLoading ? (
                    <Loader />
                ) : (
                    <img src={isSuccess ? success : error} alt="" style={{height: '190px'}} />
                )}
                <Text skin="paragraph-1" textColor="primary-700" style={{paddingTop: '30px'}}>
                    {isLoading
                        ? `Processing...`
                        : isSuccess
                        ? `Payment sent successfully`
                        : `Something went wrong`}
                </Text>
            </Flex>
        );
    }

    if (isBankTrans) {
        return (
            <BankTransferForm
                total={sum || '0'}
                token={token}
                setIsLoading={setIsLoading}
                setIsSubmitted={setIsSubmitted}
                isSubmitted={isSubmitted}
                setIsError={setIsError}
                currency={currency}
            />
        );
    }
    return (
        <CreditCardForm
            setIsLoading={setIsLoading}
            setIsSubmitted={setIsSubmitted}
            isSubmitted={isSubmitted}
            setIsSuccess={setIsSuccess}
            setIsError={setIsError}
            token={token}
            total={sum || '0'}
        />
    );
};
export default Dialog;
